import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { QcDashboardService } from '../services/qcDashboardService';

export const QC_STATUS_OPTIONS = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Being Reviewed', value: 'BEING_REVIEWED' },
  { label: 'More Info Needed', value: 'MORE_INFO_NEEDED' },
  { label: 'Completed', value: 'ACCEPTED' },
];

export const QC_COMPLETED_STATUS = 'ACCEPTED';

export const DEFAULT_QC_FILTER_MODEL = {
  status: { filterType: 'select', type: 'contains', selectedOptions: ['PENDING', 'BEING_REVIEWED'] },
};

export const FLAGGED_ITEMS_BLOCK_COMPLETION = 'Unflag all items before completing';

export interface QcDashboardRow {
  taskId: number;
  loanId: number;
  propertyAddress: string;
  status: string;
  qcSpecialist: string;
  uploadedDate: string;
  delayMinutes: number;
  itemsForRevision: number;
}

export class QcDashboardStore {
  private globalStore: GlobalStore;
  private qcDashboardService: QcDashboardService;
  gridStore: GridStore;
  reviewRow: QcDashboardRow = null;
  busyLoanIds: number[] = [];
  fetchedAt = Date.now();
  now = Date.now();

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.qcDashboardService = new QcDashboardService();
    this.gridStore = new GridStore(() => this.fetchRows());
    this.gridStore.setFilterModel(DEFAULT_QC_FILTER_MODEL, false);
    makeObservable(this, {
      fetchedAt: observable,
      now: observable,
      reviewRow: observable,
      busyLoanIds: observable,
      setBusy: action,
      setReviewRow: action,
      tick: action,
      setFetchedAt: action,
      completeReview: flow,
      sendRevision: flow,
    });
  }

  setReviewRow(row: QcDashboardRow) {
    this.reviewRow = row;
  }

  isBusy(loanId: number) {
    return this.busyLoanIds.includes(loanId);
  }

  setBusy(loanId: number, busy: boolean) {
    this.busyLoanIds = busy
      ? [...this.busyLoanIds, loanId]
      : this.busyLoanIds.filter(id => id !== loanId);
  }

  tick() {
    this.now = Date.now();
  }

  setFetchedAt(time: number) {
    this.fetchedAt = time;
    this.now = time;
  }

  currentDelayMinutes(row: QcDashboardRow) {
    if (row?.delayMinutes === null || row?.delayMinutes === undefined) {
      return null;
    }
    return row.delayMinutes + Math.max(0, Math.floor((this.now - this.fetchedAt) / 60000));
  }

  async fetchRows() {
    const { pageNumber, pageSize, sortDir, sortBy } = this.gridStore.gridData.meta;
    try {
      const response: ApiResponse = await this.qcDashboardService.getDashboard(
        pageNumber,
        pageSize,
        sortDir || undefined,
        sortBy || undefined,
        this.gridStore.gridData.meta.filters ?? {},
        this.gridStore.gridData.meta.dropdownFilters ?? {}
      );
      this.setFetchedAt(Date.now());
      return response;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error loading the Post QC Dashboard',
      });
    }
  }

  *completeReview(loanId: number) {
    if (this.isBusy(loanId)) {
      return;
    }
    this.setBusy(loanId, true);
    try {
      const response: ApiResponse = yield this.qcDashboardService.completeReview(loanId);
      if (response?.data?.data?.completed) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: `QC Review for loan ${loanId} marked as completed`,
        });
      } else {
        this.globalStore.notificationStore.showWarningNotification({
          message: `The QC Review for loan ${loanId} is no longer open`,
        });
      }
    } catch (e) {
      if (e?.error?.response?.status === 409) {
        this.globalStore.notificationStore.showWarningNotification({
          message: `Loan ${loanId}: ${e.error.response.data?.error?.message ?? FLAGGED_ITEMS_BLOCK_COMPLETION}`,
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: `Error completing the QC Review for loan ${loanId}`,
        });
      }
    } finally {
      this.setBusy(loanId, false);
    }
    yield this.gridStore.fetchGridData();
  }

  *sendRevision(loanId: number) {
    if (this.isBusy(loanId)) {
      return;
    }
    this.setBusy(loanId, true);
    try {
      yield this.qcDashboardService.sendRevision(loanId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: `Revision sent for loan ${loanId}`,
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: `Error sending the revision for loan ${loanId}`,
      });
    } finally {
      this.setBusy(loanId, false);
    }
    yield this.gridStore.fetchGridData();
  }
}
